import React, { useContext, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import AuthForm from "./components/Authorization/AuthorizationForm.jsx";
import { observer } from "mobx-react-lite";
import Charts from "./components/dash/Charts.jsx";
import "../src/scss/style.scss";
import Settings from "./components/SettingsPage/SettingsPage.jsx";
import "../src/scss/examples.scss";
import WaitersStats from "./components/WaitersStats/WaitersStats.jsx";
import PlaceholderPage from "./components/PlaceholderPage.jsx";
import DangerousOpsPage from "./components/DangerousOps/DangerousOpsPage.jsx";
import CashShiftsPage from "./components/CashShifts/CashShiftsPage.jsx";
import RevenueDetailsPage from "./components/dash/RevenueDetailsPage.jsx";
import MetricDetailsPage from "./components/dash/MetricDetailsPage.jsx";
import PaymentTypesPage from "./components/PaymentTypes/PaymentTypesPage.jsx";
import CategoriesPage from "./components/Categories/CategoriesPage.jsx";
import DishStatsPage from "./components/DishStats/DishStatsPage.jsx";
import GroupsPage from "./components/Groups/GroupsPage.jsx";
import CashAnalyticsPage from "./components/CashAnalytics/CashAnalyticsPage.jsx";
import OpsSummaryPage from "./components/OpsSummary/OpsSummaryPage.jsx";
import ComparePage from "./components/Compare/ComparePage.jsx";
import AssistantPage from "./components/Assistant/AssistantPage.jsx";
import MenuEngineeringPage from "./components/MenuEngineering/MenuEngineeringPage.jsx";
import WarehousePage from "./components/Warehouse/WarehousePage.jsx";
import PromotionsPage from "./components/Promotions/PromotionsPage.jsx";
import CustomReportsPage from "./components/CustomReports/CustomReportsPage.jsx";
import SalesChannelsPage from "./components/SalesChannels/SalesChannelsPage.jsx";
import ForecastPage from "./components/Forecast/ForecastPage.jsx";
import CostMarginPage from "./components/CostMargin/CostMarginPage.jsx";
import CompareServersPage from "./components/CompareServers/CompareServersPage.jsx";
import { Context } from "./index.js";
import RequireAuth from "./components/routing/RequireAuth.jsx";
import AppLayout from "./components/routing/AppLayout.jsx";
import AppToaster from "./components/UI/Status/AppToaster.jsx";

const App = observer(() => {
  const { store } = useContext(Context);
  useEffect(() => {
    const token = localStorage.getItem("token");
    // A token with no userId means a past session got into the broken state
    // the refresh interceptor used to be able to cause (see client.js) —
    // self-heal via a real checkAuth() instead of trusting the stale token,
    // so pages that gate their data load on "userId" stop hanging forever.
    if (token && localStorage.getItem("userId")) {
      store.setAuth(true);
      return;
    }
    store.checkAuth();
  }, [store]);

  return (
    <>
      <AppToaster />
      <Router>
        <Routes>
          <Route path="/login" element={<AuthForm />} />
          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Charts />} />
              <Route path="/dashboard" element={<Charts />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/waiters" element={<WaitersStats />} />
              <Route path="/dangerous-ops" element={<DangerousOpsPage />} />
              <Route path="/cash-shifts" element={<CashShiftsPage />} />
              <Route path="/payment-types" element={<PaymentTypesPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/dish-stats" element={<DishStatsPage />} />
              <Route path="/groups" element={<GroupsPage />} />
              <Route path="/cash-analytics" element={<CashAnalyticsPage />} />
              <Route path="/ops-summary" element={<OpsSummaryPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/assistant" element={<AssistantPage />} />
              <Route path="/menu-engineering" element={<MenuEngineeringPage />} />
              <Route path="/warehouse" element={<WarehousePage />} />
              <Route path="/promotions" element={<PromotionsPage />} />
              <Route path="/custom-reports" element={<CustomReportsPage />} />
              <Route path="/sales-channels" element={<SalesChannelsPage />} />
              <Route path="/forecast" element={<ForecastPage />} />
              <Route path="/cost-margin" element={<CostMarginPage />} />
              <Route path="/compare-servers" element={<CompareServersPage />} />
              <Route path="/dishdetails" element={<RevenueDetailsPage />} />
              <Route
                path="/itemsdetails"
                element={
                  <MetricDetailsPage
                    title="Позиции в чеках"
                    metricKey="orderItems"
                    unit=""
                    aggregate="sum"
                    color="info"
                  />
                }
              />
              <Route
                path="/avgcheckdetails"
                element={
                  <MetricDetailsPage
                    title="Средний чек"
                    metricKey="dishDiscountSumIntAverage"
                    unit="₽"
                    aggregate="avg"
                    color="primary"
                  />
                }
              />
              <Route
                path="/checkdetails"
                element={
                  <MetricDetailsPage
                    title="Чеков"
                    metricKey="uniqOrderId"
                    unit=""
                    aggregate="sum"
                    color="danger"
                  />
                }
              />
              <Route
                path="/guestsdetails"
                element={
                  <MetricDetailsPage
                    title="Гостей"
                    metricKey="guestNum"
                    unit=""
                    aggregate="sum"
                    color="primary"
                  />
                }
              />
              <Route
                path="/costdetails"
                element={
                  <MetricDetailsPage
                    title="Себестоимость"
                    metricKey="productCostBasePercent"
                    unit="%"
                    aggregate="avg"
                    color="primary"
                  />
                }
              />
              <Route
                path="/markupdetails"
                element={
                  <MetricDetailsPage
                    title="Наценка"
                    metricKey="productCostBaseMarkUp"
                    unit="%"
                    aggregate="avg"
                    color="info"
                  />
                }
              />
              <Route
                path="/avgbyguestdetails"
                element={
                  <MetricDetailsPage
                    title="Ср. выручка с гостя"
                    metricKey="averageByGuest"
                    unit="₽"
                    aggregate="avg"
                    color="danger"
                  />
                }
              />
              <Route
                path="/returndetails"
                element={
                  <MetricDetailsPage
                    title="Сумма возвратов"
                    metricKey="dishReturnSum"
                    unit="₽"
                    aggregate="sum"
                    color="primary"
                  />
                }
              />
            </Route>
          </Route>
          <Route path="*" element={<PlaceholderPage title="Страница не найдена (404)" />} />
        </Routes>
      </Router>
    </>
  );
});

export default App;
